import React, { useEffect, useState } from 'react';
import { MapPin, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { AddVendorDialog } from '@/components/po/AddVendorDialog';
import { SearchSelect } from '@/components/po/SearchSelect';
import apiClient from '@/services/apiClient';

const SRC = { vendor: '', gst: ' (from GST No.)', default: ' (not set — taken as Tamil Nadu)' };

/** Create PO vendor: search, Add New, and the vendor's state (CGST + SGST in Tamil Nadu, IGST elsewhere). onVendor(vendor|null). */
export const VendorPicker = ({ vendorCode, onChange, locked, onVendor }) => {
  const [data, setData] = useState({ vendors: [], states: [], state_codes: {}, home_state: 'Tamil Nadu' });
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = () => apiClient.get('/purchase-orders/vendors/list').then((r) => { setData(r.data); setLoaded(true); })
    .catch(() => toast.error('Could not load vendors'));
  useEffect(() => { load(); }, []);
  const v = data.vendors.find((x) => x.vendor_code === vendorCode) || null;
  useEffect(() => { onVendor(v); }, [v?.vendor_code, v?.resolved_state]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveState = async (state) => {
    try {
      await apiClient.patch(`/purchase-orders/vendors/${vendorCode}/state`, { state });
      toast.success(`${v.vendor_name}: state set to ${state}`);
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the state'); }
  };
  const igst = v && v.resolved_state !== data.home_state;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium text-gray-500">Vendor *</label>
        {!locked && <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setAdding(true)} data-testid="add-vendor-btn">
          <UserPlus className="mr-1 h-3 w-3" />Add New</Button>}
      </div>
      <SearchSelect value={vendorCode} disabled={locked} loading={!loaded} onChange={onChange} placeholder="Search vendor" searchPlaceholder="Search vendor name, code or GST…"
        empty='No vendor found — click "Add New"' testId="vendor-select"
        options={data.vendors.map((x) => ({ value: x.vendor_code, label: x.vendor_name, sub: [x.vendor_code, x.gst_no, x.resolved_state].filter(Boolean).join(' · ') }))} />
      {locked && <p className="mt-1 text-[11px] text-gray-400">Vendor is locked after PO creation.</p>}
      {v && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="vendor-state-info">
          <MapPin className="h-3 w-3 text-gray-400" />
          <span className="text-gray-600"><span className="font-semibold" data-testid="vendor-state">{v.resolved_state}</span>{SRC[v.state_source] || ''}</span>
          <span className={`rounded px-1.5 py-0.5 font-semibold ${igst ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid="vendor-gst-type">{igst ? 'IGST' : 'CGST + SGST'}</span>
          {!editing && <button type="button" onClick={() => setEditing(true)} className="text-indigo-600 hover:underline" data-testid="vendor-state-edit">{v.state ? 'Change' : 'Set state'}</button>}
        </div>)}
      {v && editing && (
        <div className="mt-1 flex items-center gap-1">
          <div className="flex-1"><SearchSelect value={v.state || ''} options={data.states.map((s) => ({ value: s, label: s }))} onChange={saveState}
            placeholder="Pick the vendor's state" searchPlaceholder="Search state…" testId="vendor-state-select" /></div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="vendor-state-cancel">Cancel</Button>
        </div>)}
      <AddVendorDialog open={adding} states={data.states} stateCodes={data.state_codes || {}} onClose={() => setAdding(false)}
        onCreated={(nv) => { setAdding(false); load().then(() => onChange(nv.vendor_code)); }} />
    </div>
  );
};

export default VendorPicker;
